import { createFileRoute } from "@tanstack/react-router";
import assert from "assert";
import { DM_Masonry_BrickKing } from "common/materials/library/designMaterialPath";
import { assertIsNotNull, ensureDefined } from "common/utils/assertOfType";
import { polyLine2New } from "geom/polyLine2Types";
import { defaultCameraSettings } from "gfx/CameraSettings";
import { polyLine2ToTHREEStroke, scaleBufferGeometryUVs, xyPlane } from "gfx/convert";
import { LineStrokeMaterialFactory } from "gfx/materials/lineStrokeMaterial";
import { pixelCheckerboardFragmentShader } from "gfx/materials/shaders/pixelCheckerboardFragmentShader";
import PerspectiveControls from "gfx/PerspectiveControls";
import { UVVertexShader } from "gfx/threejs/higharc/UVVertexShader";
import type THREEViewerLite from "gfx/THREEViewerLite";
import { activeTHREEViewerServices } from "gfx/THREEViewerServices";
import type { THREEViewerSystem } from "gfx/THREEViewerSystem";
import { SharedWebGLCanvas } from "lib/components/gfx/SharedWebGLCanvas";
import useTHREEViewerLite from "lib/components/studio/editorViewer/useTHREEViewerLite";
import useTHREEViewerSystem from "lib/components/studio/useTHREEViewerSystem";
import getPageTitle from "lib/utils/getPageTitle";
import { useEffect, useRef, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import * as THREE from "three";
import withAuthentication from "../../components/withAuthentication";

const cubeGeometry = new THREE.BoxGeometry(500, 500, 500);
scaleBufferGeometryUVs(cubeGeometry, 20);

const checkerboardMaterial = new THREE.ShaderMaterial({
  vertexShader: UVVertexShader,
  fragmentShader: pixelCheckerboardFragmentShader,
});

function SharedWebGLContextViewers({
  sharedCanvasCornersRounded,
  setSharedCanvasCornersRounded,
}: {
  sharedCanvasCornersRounded: boolean;
  setSharedCanvasCornersRounded: (value: boolean) => void;
}) {
  const viewerContainerRefA = useRef<HTMLDivElement>(null);
  const viewerRefA = useRef<THREEViewerLite>(null);
  useTHREEViewerLite({ viewerRef: viewerRefA, viewerContainerRef: viewerContainerRefA, viewName: "a" });

  const viewerContainerRefB = useRef<HTMLDivElement>(null);
  const viewerRefB = useRef<THREEViewerLite>(null);
  useTHREEViewerLite({
    viewerRef: viewerRefB,
    viewerContainerRef: viewerContainerRefB,
    viewName: "b",
  });

  const [viewerBIsPictureInPicture, setViewerBIsPictureInPicture] = useState(true);

  const viewerContainerRefLeft = useRef<HTMLDivElement>(null);
  const viewerSystemLeft = useRef<THREEViewerSystem>(null);
  const { renderID: renderIDLeft } = useTHREEViewerSystem({
    viewerSystem: viewerSystemLeft,
    viewerContainerRef: viewerContainerRefLeft,
    setLoading: () => {},
  });

  const viewerContainerRefRight = useRef<HTMLDivElement | null>(null);
  const viewerSystemRight = useRef<THREEViewerSystem | null>(null);
  const { renderID: renderIDRight } = useTHREEViewerSystem({
    viewerSystem: viewerSystemRight,
    viewerContainerRef: viewerContainerRefRight,
    setLoading: () => {},
  });

  const cubeMeshB = useRef<THREE.Object3D | undefined>(undefined);
  const cubeMeshC = useRef<THREE.Object3D | undefined>(undefined);

  const [screenshotURL, setScreenshotURL] = useState("");
  const updateScreenshot = async () => {
    if (!viewerSystemLeft.current) {
      return;
    }
    const pixelWidth = 800;
    const pixelHeight = 600;
    const camera = new THREE.PerspectiveCamera(50, 800 / 600, 1, 10000);
    camera.position.set(1000, 500, 300);
    camera.up.set(0, 0, 1);
    camera.lookAt(new THREE.Vector3(0, 0, 0));
    camera.updateMatrixWorld();

    const viewerRenderer = viewerSystemLeft.current.viewer.viewerRenderer;

    assert(viewerSystemLeft.current.viewer.scene.ssfxRendererScene);
    const url = activeTHREEViewerServices.imageRenderer.renderImageSync(
      viewerSystemLeft.current.viewer.scene.ssfxRendererScene,
      camera,
      ensureDefined(viewerRenderer.ssfxParamsManager).getRenderParams(),
      {
        width: pixelWidth,
        height: pixelHeight,
      },
    );
    setScreenshotURL(url ?? "");
  };

  const loseContext = () => {
    if (!viewerSystemLeft.current) {
      return;
    }
    const loseContextExt = viewerSystemLeft.current.viewer.viewerRenderer?.renderer
      .getContext()
      .getExtension("WEBGL_lose_context");
    loseContextExt?.loseContext();
  };

  const swapPictureInPictureViewer = () => {
    if (viewerRefB.current && viewerRefA.current) {
      const viewerA = viewerRefA.current;
      const viewerB = viewerRefB.current;
      if (viewerB.renderOrder === 1) {
        viewerB.renderOrder = 0;
        viewerA.renderOrder = 1;
        setViewerBIsPictureInPicture(false);
      } else {
        viewerB.renderOrder = 1;
        viewerA.renderOrder = 0;
        setViewerBIsPictureInPicture(true);
      }
    }
  };

  useEffect(() => {
    if (viewerRefA.current && viewerSystemLeft.current && viewerSystemRight.current && viewerRefB.current) {
      const viewerA = viewerRefA.current;
      viewerA.setSceneBackground(new THREE.Color("#4dd689"));
      viewerA.scene.clear();
      viewerA.scene.add(
        polyLine2ToTHREEStroke({
          path: polyLine2New([-100, -100, 100, -100, 100, 100, -100, 100, -100, -100]),
          plane: xyPlane,
          material: LineStrokeMaterialFactory.instance.getMaterial({}),
        }),
      );
      viewerA.renderOrder = viewerBIsPictureInPicture ? 0 : 1;

      // If the checkerboard is sharp on screen, then everything is fine.
      // If the checkerboard is blurry, then the render target dimensions might be subtly wrong.
      const checkerboardMesh = new THREE.Mesh(new THREE.PlaneGeometry(100, 100), checkerboardMaterial);
      viewerA.scene.add(checkerboardMesh);

      const viewerB = viewerRefB.current;
      viewerB.renderOrder = viewerBIsPictureInPicture ? 1 : 0;
      viewerB.setSceneBackground(new THREE.Color("#7799aa"));

      const viewerLeft = viewerSystemLeft.current.viewer;
      viewerLeft.setSceneBackground(new THREE.Color("#736968"));
      viewerLeft.skyboxVisible = false;

      const viewerRight = viewerSystemRight.current.viewer;
      viewerRight.setSceneBackground(new THREE.Color("#888888"));
      viewerRight.skyboxVisible = false;

      void (async () => {
        assertIsNotNull(viewerSystemLeft.current);
        const brickMaterialB = await viewerSystemLeft.current.materialFactory.getDesignMaterial(
          DM_Masonry_BrickKing.L.Brick_Acme.Brick_Acme_AdobeWells,
        );
        viewerLeft.scene.clear();
        cubeMeshB.current = new THREE.Mesh(cubeGeometry, brickMaterialB);
        viewerLeft.scene.add(cubeMeshB.current);

        const brickMaterialC = await viewerSystemLeft.current.materialFactory.getDesignMaterial(
          DM_Masonry_BrickKing.L.Brick_Acme.Brick_Acme_Augusta,
        );
        viewerRight.scene.clear();
        cubeMeshC.current = new THREE.Mesh(cubeGeometry, brickMaterialC);
        viewerRight.scene.add(cubeMeshC.current);
      })();

      viewerLeft.setCameraSettings({
        ...defaultCameraSettings,
        targetX: 0,
        targetY: 0,
        targetZ: 0,
      });
      const cameraControlsB = new PerspectiveControls(viewerLeft.camera, viewerLeft.container, 0, 0, 0);
      viewerLeft.setControls(cameraControlsB);

      viewerRight.setCameraSettings({
        ...defaultCameraSettings,
        targetX: 0,
        targetY: 0,
        targetZ: 0,
      });
      const cameraControlsC = new PerspectiveControls(viewerRight.camera, viewerRight.container, 0, 0, 0);
      viewerRight.setControls(cameraControlsC);

      let frameCallback: number;

      function frame() {
        // Rotate for 1 second, then stop for 1 second.
        // Should see AA resolve to a nice state while the rotation is stopped.
        if (Math.floor(Date.now() / 1000) % 2 === 0) {
          if (cubeMeshB.current) {
            cubeMeshB.current.rotateZ(0.05);
            cubeMeshB.current.updateMatrix();
            cubeMeshB.current.updateMatrixWorld();
            if (viewerSystemLeft.current) {
              viewerSystemLeft.current.viewer.markRendererForUpdate();
            }
          }
        }
        if (Math.floor(Date.now() / 1000) % 3 === 1) {
          if (cubeMeshC.current) {
            cubeMeshC.current.rotateZ(-0.03);
            cubeMeshC.current.updateMatrix();
            cubeMeshC.current.updateMatrixWorld();
            if (viewerSystemRight.current) {
              viewerSystemRight.current.viewer.markRendererForUpdate();
            }
          }
        }
        frameCallback = window.requestAnimationFrame(frame);
      }

      frameCallback = window.requestAnimationFrame(frame);

      return () => {
        assertIsNotNull(viewerSystemLeft.current);
        window.cancelAnimationFrame(frameCallback);
        viewerSystemLeft.current?.viewer.scene.clear();
        viewerSystemRight.current?.viewer.scene.clear();
      };
    }
    return () => {};
  }, [renderIDLeft, renderIDRight]);

  const mainViewerStyle: React.CSSProperties = { width: "100%", height: "50%", position: "absolute", top: 0, left: 0 };
  const pictureInPictureViewerStyle: React.CSSProperties = {
    width: 200,
    height: 150,
    position: "absolute",
    top: 10,
    left: 10,
    zIndex: 1,
  };

  return (
    <div style={{ height: "100%" }}>
      <div
        ref={viewerContainerRefA}
        id="viewerA"
        style={viewerBIsPictureInPicture ? mainViewerStyle : pictureInPictureViewerStyle}
      ></div>
      <div
        ref={viewerContainerRefB}
        id="viewerB"
        style={viewerBIsPictureInPicture ? pictureInPictureViewerStyle : mainViewerStyle}
      ></div>
      <div style={{ position: "absolute", top: 170, left: 10, width: 200 }}>
        <button onClick={swapPictureInPictureViewer} style={{ width: 200 }}>
          Swap picture in picture viewer
        </button>
      </div>
      <div
        ref={viewerContainerRefLeft}
        id="viewerLeft"
        style={{ width: "50%", height: "50%", position: "absolute", top: "50%", left: 0 }}
      ></div>
      <div
        ref={viewerContainerRefRight}
        id="viewerRight"
        style={{ width: "50%", height: "50%", position: "absolute", top: "50%", left: "50%" }}
      ></div>
      <div style={{ position: "absolute", top: 10, right: 10, width: 200, height: 150, background: "grey" }}>
        {screenshotURL && <img style={{ width: "100%" }} src={screenshotURL} />}
        <button style={{ position: "absolute", bottom: 0, left: 0 }} onClick={updateScreenshot}>
          Update screenshot
        </button>
      </div>
      <div style={{ position: "absolute", top: 170, right: 10, width: 200 }}>
        <button onClick={loseContext} style={{ width: "100%" }}>
          Lose WebGL context
        </button>
        <button onClick={() => setSharedCanvasCornersRounded(!sharedCanvasCornersRounded)} style={{ width: "100%" }}>
          {sharedCanvasCornersRounded ? "Make shared canvas corners square" : "Make shared canvas corners rounded"}
        </button>
      </div>
    </div>
  );
}

function WebGLContextSharingPage() {
  const [sharedCanvasCornersRounded, setSharedCanvasCornersRounded] = useState(false);

  return (
    <div style={{ width: "100%", height: "100%", overflow: "hidden" }}>
      <Head>
        <title>{getPageTitle("WebGL Context Sharing Test")}</title>
        <meta name="robots" content="noindex" />
      </Head>
      <SharedWebGLCanvas canvasStyle={{ borderRadius: sharedCanvasCornersRounded ? "50px" : "0px" }}>
        <SharedWebGLContextViewers
          sharedCanvasCornersRounded={sharedCanvasCornersRounded}
          setSharedCanvasCornersRounded={setSharedCanvasCornersRounded}
        ></SharedWebGLContextViewers>
      </SharedWebGLCanvas>
    </div>
  );
}

export const Route = createFileRoute("/debug/webgl-context-sharing")({
  component: withAuthentication(WebGLContextSharingPage, { superAdmin: true }),
});
